import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";

import { ConfirmDialog } from "@/components/confirm-dialog";
import { useStages } from "@/features/stages/hooks/use-stages.ts";
import { useDownloadRdoPdf } from "../../hooks/use-download-rdo-pdf.ts";
import {
	useDeleteRdo,
	useFinalizeRdo,
	useOpenRdoRevision,
	useRetryRdo,
} from "../../hooks/use-rdo-actions.ts";
import { useRdoActivities } from "../../hooks/use-rdo-activities.ts";
import { useRdoPhotos } from "../../hooks/use-rdo-photos.ts";
import { useRdoPreview } from "../../hooks/use-rdo-preview.ts";
import { useRdoScheduledServices } from "../../hooks/use-rdo-scheduled-services.ts";
import { useUpdateRdo } from "../../hooks/use-update-rdo.ts";
import type { Rdo, RdoActivity } from "../../types.ts";
import { rdoErrorMessage } from "../../utils/errors.ts";
import { rdoPdfFilename } from "../../utils/format.ts";
import { isRdoEditable } from "../../utils/status.ts";
import { RdoPreviewDialog } from "../rdo-preview-dialog";
import { RdoStatusBar } from "../rdo-status-bar";
import { ActivitiesSection } from "./activities-section.tsx";
import { CrewSection } from "./crew-section.tsx";
import { IdentificationSection } from "./identification-section.tsx";
import { ObservationsSection } from "./observations-section.tsx";
import { PerformanceSection } from "./performance-section.tsx";
import { ScheduledServicesSection } from "./scheduled-services-section.tsx";
import { SignaturesSection } from "./signatures-section.tsx";
import { SummarySection } from "./summary-section.tsx";

function waitForPendingMutations(queryClient: QueryClient): Promise<void> {
	return new Promise((resolve) => {
		if (queryClient.isMutating() === 0) {
			resolve();
			return;
		}
		const unsubscribe = queryClient.getMutationCache().subscribe(() => {
			if (queryClient.isMutating() > 0) return;
			unsubscribe();
			resolve();
		});
	});
}

interface RdoEditorProps {
	rdo: Rdo;
	constructionId: string;
	obraKey: string;
	obraName: string;
	canteiroName?: string;
	canUpdate: boolean;
	canFinalize: boolean;
	canDelete: boolean;
}

export function RdoEditor({
	rdo,
	constructionId,
	obraKey,
	obraName,
	canteiroName,
	canUpdate,
	canFinalize,
	canDelete,
}: RdoEditorProps) {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const readOnly = !isRdoEditable(rdo.status) || !canUpdate;

	const stagesQuery = useStages(constructionId);
	const stages = stagesQuery.data?.stages ?? [];

	const updateRdo = useUpdateRdo(constructionId, rdo.id);
	const activities = useRdoActivities(constructionId, rdo.id);
	const photos = useRdoPhotos(constructionId, rdo.id);
	const scheduledServices = useRdoScheduledServices(constructionId, rdo.id);
	const preview = useRdoPreview(constructionId, rdo.id);
	const finalize = useFinalizeRdo(constructionId, rdo.id);
	const retry = useRetryRdo(constructionId, rdo.id);
	const revision = useOpenRdoRevision(constructionId, rdo.id);
	const remove = useDeleteRdo(constructionId, rdo.id);
	const download = useDownloadRdoPdf(constructionId, rdo.id);

	const [confirmDelete, setConfirmDelete] = useState(false);
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);
	const [previewing, setPreviewing] = useState(false);
	const [activityToDelete, setActivityToDelete] = useState<RdoActivity | null>(
		null,
	);

	async function handleFinalize() {
		await updateRdo.flush();
		finalize.mutate(undefined, {
			onSuccess: () =>
				toast.success("Relatório de Obra enviado para geração do PDF."),
			onError: (err) =>
				toast.error(
					rdoErrorMessage(
						err,
						"Não foi possível finalizar o Relatório de Obra.",
					),
				),
		});
	}

	async function handlePreview() {
		if (previewing) return;
		setPreviewing(true);
		try {
			await updateRdo.flush();
			await waitForPendingMutations(queryClient);
			setPreviewUrl(await preview.mutateAsync());
		} catch {
			toast.error("Não foi possível gerar a prévia. Tente novamente.");
		} finally {
			setPreviewing(false);
		}
	}

	function handleRetry() {
		retry.mutate(undefined, {
			onSuccess: () => toast.success("Geração do PDF reiniciada."),
			onError: (err) =>
				toast.error(
					rdoErrorMessage(
						err,
						"Não foi possível reprocessar o Relatório de Obra.",
					),
				),
		});
	}

	function handleOpenRevision() {
		revision.mutate(undefined, {
			onSuccess: (next) => {
				toast.success("Revisão aberta como rascunho.");
				navigate(`/${obraKey}/rdo/${next.id}`);
			},
			onError: (err) =>
				toast.error(rdoErrorMessage(err, "Não foi possível abrir a revisão.")),
		});
	}

	function handleDelete() {
		remove.mutate(undefined, {
			onSuccess: () => {
				toast.success("Rascunho excluído.");
				navigate(`/${obraKey}/rdo`);
			},
			onError: (err) => {
				setConfirmDelete(false);
				toast.error(
					rdoErrorMessage(err, "Não foi possível excluir o rascunho."),
				);
			},
		});
	}

	function handleDownload() {
		download.mutate(rdoPdfFilename(rdo), {
			onError: (err) =>
				toast.error(rdoErrorMessage(err, "Não foi possível baixar o PDF.")),
		});
	}

	function handleDeleteActivity() {
		if (!activityToDelete) return;
		activities.remove.mutate(activityToDelete.id, {
			onSettled: () => setActivityToDelete(null),
			onError: (err) =>
				toast.error(
					rdoErrorMessage(err, "Não foi possível remover a atividade."),
				),
		});
	}

	const saving =
		updateRdo.isPending ||
		activities.update.isPending ||
		activities.reorder.isPending ||
		scheduledServices.update.isPending ||
		scheduledServices.reorder.isPending ||
		scheduledServices.remove.isPending ||
		photos.updateCaption.isPending ||
		photos.detach.isPending;

	return (
		<div className="rdo-editor" data-readonly={readOnly}>
			<RdoStatusBar
				rdo={rdo}
				obraKey={obraKey}
				canUpdate={canUpdate}
				canFinalize={canFinalize}
				canDelete={canDelete}
				saving={saving}
				pending={{
					finalize: finalize.isPending,
					retry: retry.isPending,
					revision: revision.isPending,
					remove: remove.isPending,
					download: download.isPending,
					preview: previewing,
				}}
				onFinalize={handleFinalize}
				onRetry={handleRetry}
				onOpenRevision={handleOpenRevision}
				onDelete={() => setConfirmDelete(true)}
				onDownload={handleDownload}
				onPreview={handlePreview}
			/>

			<IdentificationSection
				rdo={rdo}
				obraName={obraName}
				canteiroName={canteiroName}
				readOnly={readOnly}
				onTechnicalManagerChange={(value) =>
					updateRdo.save({ technicalManager: value })
				}
			/>
			<SummarySection
				rdo={rdo}
				readOnly={readOnly}
				onSaveNow={updateRdo.saveNow}
			/>
			<ActivitiesSection
				rdo={rdo}
				constructionId={constructionId}
				stages={stages}
				readOnly={readOnly}
				creating={activities.create.isPending}
				onCreate={(input) =>
					activities.create.mutateAsync(input).catch((err) => {
						toast.error(
							rdoErrorMessage(err, "Não foi possível adicionar a atividade."),
						);
					})
				}
				onUpdate={(activityId, input) =>
					activities.update.mutate(
						{ activityId, input },
						{
							onError: (err) =>
								toast.error(
									rdoErrorMessage(err, "Não foi possível salvar a atividade."),
								),
						},
					)
				}
				onDelete={setActivityToDelete}
				onReorder={(ids) => activities.reorder.mutate(ids)}
				onAttachPhotos={(activityId, inputs) =>
					photos.attach.mutateAsync({ activityId, inputs })
				}
				onUpdateCaption={(photoId, caption) =>
					photos.updateCaption.mutate({ photoId, caption })
				}
				onDetachPhoto={(photoId) =>
					photos.detach.mutate(photoId, {
						onError: (err) =>
							toast.error(
								rdoErrorMessage(err, "Não foi possível remover a foto."),
							),
					})
				}
				onPhotoSizeChange={(photoSize) => updateRdo.saveNow({ photoSize })}
			/>
			<ScheduledServicesSection
				rdo={rdo}
				stages={stages}
				readOnly={readOnly}
				creating={scheduledServices.create.isPending}
				onCreate={(input) =>
					scheduledServices.create.mutateAsync(input).catch((err) => {
						toast.error(
							rdoErrorMessage(err, "Não foi possível adicionar o serviço."),
						);
					})
				}
				onUpdate={(serviceId, input) =>
					scheduledServices.update.mutate(
						{ serviceId, input },
						{
							onError: (err) =>
								toast.error(
									rdoErrorMessage(err, "Não foi possível salvar o serviço."),
								),
						},
					)
				}
				onDelete={(serviceId) =>
					scheduledServices.remove.mutate(serviceId, {
						onError: (err) =>
							toast.error(
								rdoErrorMessage(err, "Não foi possível remover o serviço."),
							),
					})
				}
				onReorder={(ids) => scheduledServices.reorder.mutate(ids)}
			/>
			<PerformanceSection
				rdo={rdo}
				readOnly={readOnly}
				onToggle={(enabled) =>
					updateRdo.saveNow({ performanceEnabled: enabled })
				}
			/>
			<ObservationsSection
				rdo={rdo}
				readOnly={readOnly}
				onChange={(value) => updateRdo.save({ observations: value })}
			/>
			<SignaturesSection
				rdo={rdo}
				readOnly={readOnly}
				onChange={(patch) => updateRdo.save({ signatures: patch })}
			/>
			<CrewSection
				rdo={rdo}
				readOnly={readOnly}
				onChange={(enabled) => updateRdo.saveNow({ crewAnnexEnabled: enabled })}
			/>

			{previewUrl && (
				<RdoPreviewDialog
					objectUrl={previewUrl}
					onClose={() => setPreviewUrl(null)}
				/>
			)}
			<ConfirmDialog
				open={confirmDelete}
				onOpenChange={setConfirmDelete}
				title="Excluir rascunho?"
				description="O Relatório de Obra deste dia será removido e o número de sequência não será reaproveitado."
				confirmLabel="Excluir"
				destructive
				isPending={remove.isPending}
				onConfirm={handleDelete}
			/>
			<ConfirmDialog
				open={!!activityToDelete}
				onOpenChange={(open) => {
					if (!open) setActivityToDelete(null);
				}}
				title="Remover atividade?"
				description={
					activityToDelete
						? `A atividade “${activityToDelete.title}” e suas fotos saem deste Relatório de Obra.`
						: ""
				}
				confirmLabel="Remover"
				destructive
				isPending={activities.remove.isPending}
				onConfirm={handleDeleteActivity}
			/>
		</div>
	);
}
